// Client wrapper for the predict edge function.
// Surfaces quota_exceeded as a structured error the UI can render.

import { supabase } from "@/integrations/supabase/client";
import type { PredictionResult } from "./predictionTypes";

export class QuotaExceededError extends Error {
  tier: string;
  used: number;
  limit: number;
  window: "day" | "month";
  resets_at: string;
  constructor(opts: { tier: string; used: number; limit: number; window: "day" | "month"; resets_at: string; message: string }) {
    super(opts.message);
    this.name = "QuotaExceededError";
    this.tier = opts.tier;
    this.used = opts.used;
    this.limit = opts.limit;
    this.window = opts.window;
    this.resets_at = opts.resets_at;
  }
}

export async function callPredict(rawInput: string, opts?: { cid?: string | null }): Promise<PredictionResult> {
  const { data, error } = await supabase.functions.invoke("predict", {
    body: { input: rawInput, cid: opts?.cid ?? null },
  });

  // supabase.functions.invoke returns FunctionsHttpError on non-2xx; the response body
  // still arrives in `data` for many setups. Normalize both paths.
  const payload: any = data ?? null;
  if (payload && payload.error === "quota_exceeded" && payload.quota) {
    throw new QuotaExceededError({
      tier: payload.quota.tier,
      used: payload.quota.used,
      limit: payload.quota.limit,
      window: payload.quota.window,
      resets_at: payload.quota.resets_at,
      message: payload.quota.message ?? "Quota exceeded.",
    });
  }
  if (error) {
    // FunctionsHttpError: ctx is a Response. Body may only be readable once and
    // json() can throw if already consumed. Use text() defensively, then parse.
    const ctx: any = (error as any).context;
    let body: any = null;
    if (ctx) {
      try {
        if (typeof ctx.text === "function") {
          const raw = await ctx.text();
          if (raw) {
            try { body = JSON.parse(raw); } catch { /* not json */ }
          }
        } else if (typeof ctx.json === "function") {
          try { body = await ctx.json(); } catch { /* swallow */ }
        }
      } catch { /* swallow */ }
    }
    if (body?.error === "quota_exceeded" && body.quota) {
      throw new QuotaExceededError({
        tier: body.quota.tier,
        used: body.quota.used,
        limit: body.quota.limit,
        window: body.quota.window,
        resets_at: body.quota.resets_at,
        message: body.quota.message ?? "Quota exceeded.",
      });
    }
    // Surface a cleaner message than "Edge function returned 429: Error"
    if (ctx?.status === 429) {
      throw new Error(body?.message ?? "Rate limit reached. Please try again shortly.");
    }
    throw error;
  }
  return payload as PredictionResult;
}
